import React, { useEffect } from 'react';
import { Modal, Button, Alert } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import ProfileHelper from 'utils/apiHelpers/profilehelper.ts';
import { addProfile } from 'redux/reducers/profiles';
import { wsSendNewProfile } from 'redux/middlewares/signalr';
import { profileTypesSelector } from 'redux/reducers/profileTypes';
import ProfileTypeForm from './ProfileTypeForm/ProfileTypeForm';
import AppDomainInput from './AppDomainInput';
import ProfileInputs from './ProfileInputs/ProfileInputs';
import CreationSuccess from './CreationSuccess/CreationSuccess';
import DocWarning from './DocWarning/DocWarning';
import {
  determineNextStep,
  determinePreviousStep,
  isLastStep,
  isAForm,
  errorInPreviousStep,
} from './utils';
import {
  ENUM_STEP_PROFILETYPE,
  ENUM_STEP_AZDOMAINS,
  ENUM_STEP_APPDOMAIN,
  ENUM_STEP_CREATIONSUCCESS,
  ENUM_STEP_DOCWARNING,
  ENUM_STEP_PROFILEINPUTS,
  ENUM_STEP_URI,
  ENUM_STEP_ERROR_409,
} from './formStepsEnum';
// eslint-disable-next-line import/no-unresolved, import/extensions
import DomainsInput from './DomainsInput';
import './styles.less';

const NewProfileForm = ({ visible, onFormCompleted, onCancel }) => {
  const [currentStep, setCurrentStep] = React.useState(0);
  const [formData, setFormData] = React.useState({});
  const [formErrors, setFormErrors] = React.useState({});
  const [formLoading, setFormLoading] = React.useState(false);
  const [configURL, setConfigURL] = React.useState('');
  const dispatch = useDispatch();
  const profileTypeList = useSelector(profileTypesSelector);
  const { t } = useTranslation();

  let currentForm;
  const currentFormRef = React.createRef();
  const currentProfileType = profileTypeList.find(
    (p) => p.profileTypeId === formData.profileTypeId,
  );

  const resetForm = () => {
    setFormData({});
    setFormErrors({});
    setConfigURL('');
    setFormLoading(false);
    setCurrentStep(0);
  };

  useEffect(() => {
    if (visible) {
      resetForm();
    }
  }, [visible]);

  const showBackButton = () =>
    currentStep !== ENUM_STEP_PROFILETYPE && currentStep !== ENUM_STEP_CREATIONSUCCESS;

  const saveFormData = () => {
    const form = currentFormRef.current;
    const values = form.getFieldsValue();
    setFormData((prevData) => ({ ...prevData, ...values }));
  };

  const saveValidFormData = async () =>
    new Promise((resolve) => {
      currentFormRef.current
        .validateFields()
        .then((values) => {
          setFormData((prevData) => ({ ...prevData, ...values }));
          resolve({ values });
        })
        .catch((error) => {
          resolve({ error });
        });
    });

  const handleBackClick = () => {
    if (isAForm(currentStep)) {
      saveFormData();
    }
    setCurrentStep(determinePreviousStep(currentStep, currentProfileType));
  };

  const handleProfileTypeChange = (profileTypeId) => {
    setFormData((prevData) => ({ ...prevData, profileTypeId }));
  };

  const handleContinueClick = async () => {
    if (isAForm(currentStep)) {
      const { values } = await saveValidFormData();
      if (values) {
        setCurrentStep(determineNextStep(currentStep, currentProfileType));
      }
    } else {
      setCurrentStep(determineNextStep(currentStep, currentProfileType));
    }
  };

  const createProfile = async (options) => {
    const result = await ProfileHelper.create(options);

    dispatch(addProfile(result.data.profile));
    dispatch(wsSendNewProfile(result.data.profile));
    if (result.data.configURL) {
      setConfigURL(result.data.configURL);
    }
  };

  const handleCreateClick = async (useFormValues = true) => {
    setFormLoading(true);

    let createOptions;
    let validFormValues;

    if (useFormValues) {
      const { values } = await saveValidFormData();
      validFormValues = values;
      createOptions = {
        profileTypeId: formData.profileTypeId,
        formValues: { ...formData, ...values },
      };
    } else {
      createOptions = {
        profileTypeId: formData.profileTypeId,
        formValues: {},
      };
    }

    if (!useFormValues || validFormValues) {
      try {
        await createProfile(createOptions);
        setCurrentStep(ENUM_STEP_CREATIONSUCCESS);
      } catch (error) {
        if (error.response.status === 400) {
          setFormErrors(error.response.data);
          if (
            currentStep !== ENUM_STEP_APPDOMAIN &&
            errorInPreviousStep(currentProfileType, error.response.data)
          ) {
            setCurrentStep(ENUM_STEP_APPDOMAIN);
          }
        } else if (error.response.status === 409) {
          setCurrentStep(ENUM_STEP_ERROR_409);
        }
      }
    }

    setFormLoading(false);
  };

  const handleCreateNoFormClick = async () => {
    await handleCreateClick(false);
  };

  const handleCancelClick = () => {
    onCancel();
  };

  const handleCloseClick = () => {
    onFormCompleted();
  };

  const handleInputChange = (inputId) => {
    if (formErrors[inputId]) {
      setFormErrors((errors) => ({ ...errors, [inputId]: '' }));
    }
  };

  const modalTitle =
    currentStep === ENUM_STEP_PROFILETYPE
      ? t('newProfileForm.serviceToConnectTitle')
      : t('newProfileForm.connectionTo', { provider: currentProfileType.profileTypeId });
  let okButtonLabel;
  let okButtonOnClick;
  let showModalFooter = true;
  const isLastStepResult = isLastStep(currentStep, currentProfileType);
  const backButton = showBackButton() ? (
    <Button key="close" onClick={handleBackClick}>
      {t('forms.back')}
    </Button>
  ) : null;

  switch (currentStep) {
    case ENUM_STEP_PROFILETYPE:
      okButtonLabel = isLastStepResult ? t('forms.create') : t('forms.continue');
      okButtonOnClick = isLastStepResult ? handleCreateNoFormClick : handleContinueClick;
      currentForm = (
        <ProfileTypeForm
          values={formData}
          onChange={handleProfileTypeChange}
          initValue={formData.profileTypeId}
          onSubmit={(e) => {
            e?.preventDefault();
            okButtonOnClick();
          }}
        />
      );
      break;
    case ENUM_STEP_DOCWARNING:
      showModalFooter = false;
      currentForm = (
        <DocWarning
          onOk={handleContinueClick}
          onCancel={handleBackClick}
          profileTypeId={currentProfileType.profileTypeId}
        />
      );
      break;
    case ENUM_STEP_AZDOMAINS:
      okButtonLabel = isLastStepResult ? t('forms.create') : t('forms.continue');
      okButtonOnClick = isLastStepResult ? handleCreateClick : handleContinueClick;
      currentForm = (
        <DomainsInput
          form={currentFormRef}
          value={formData.azDomains}
          onChange={handleInputChange}
          profileTypeId={currentProfileType.profileTypeId}
        />
      );
      break;
    case ENUM_STEP_APPDOMAIN:
      okButtonLabel = isLastStepResult ? t('forms.create') : t('forms.continue');
      okButtonOnClick = isLastStepResult ? handleCreateClick : handleContinueClick;
      currentForm = (
        <AppDomainInput
          form={currentFormRef}
          template={currentProfileType.editableFields.customAppDomain}
          value={formData.customAppDomain}
          error={formErrors.customAppDomain}
          onChange={handleInputChange}
          profileTypeId={currentProfileType.profileTypeId}
          onSubmit={(e) => {
            e?.preventDefault();
            okButtonOnClick();
          }}
        />
      );
      break;
    case ENUM_STEP_PROFILEINPUTS:
      okButtonLabel = t('forms.create');
      okButtonOnClick = handleCreateClick;
      currentForm = (
        <ProfileInputs
          hideCustomAppDomain
          form={currentFormRef}
          editableFields={currentProfileType.editableFields}
          errors={formErrors}
          onChange={handleInputChange}
          onSubmit={handleCreateClick}
          values={formData}
        />
      );
      break;
    case ENUM_STEP_URI:
      break;
    case ENUM_STEP_CREATIONSUCCESS:
      okButtonLabel = t('forms.close');
      okButtonOnClick = handleCloseClick;
      currentForm = (
        <CreationSuccess configUrl={configURL} providerId={currentProfileType.profileTypeId} />
      );
      break;
    case ENUM_STEP_ERROR_409:
      okButtonLabel = t('forms.close');
      okButtonOnClick = handleCloseClick;
      currentForm = (
        <Alert
          message={t('errors.errorNumber', { number: 409 })}
          description={t('errors.409message')}
          type="error"
          showIcon
        />
      );
      break;
    default:
      break;
  }

  return (
    <Modal
      title={modalTitle}
      visible={visible}
      onCancel={handleCancelClick}
      width={780}
      footer={
        showModalFooter ? (
          <div className="new-profile-form__modal-footer">
            <div>
              <a rel="noopener noreferrer" target="_blank" href={t('urls.baseDocUrl')}>
                {t('newProfileForm.helpButton')}
              </a>
            </div>
            <div>
              {backButton}
              <Button
                key="ok"
                type="primary"
                onClick={okButtonOnClick}
                loading={formLoading}
                disabled={!formData.profileTypeId}
              >
                {okButtonLabel}
              </Button>
            </div>
          </div>
        ) : null
      }
      destroyOnClose
    >
      <div className="new-profile-form__form-container">{currentForm}</div>
    </Modal>
  );
};

NewProfileForm.propTypes = {
  visible: PropTypes.bool.isRequired,
  onFormCompleted: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default NewProfileForm;
